import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { CreditCard, ShoppingBag, Truck, Zap } from 'lucide-react';

const BusinessModelSection: React.FC = () => {
  return (
    <section className="py-32 bg-theme-bg relative">
      <Container>
        <div className="text-center mb-24">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-theme-success/10 border border-theme-success/20 text-theme-success text-xs font-bold uppercase tracking-widest mb-6">
            <span>Economic Architecture</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-theme-text-primary mb-6">Scalable. Profitable. <br /> <span className="text-theme-success">Defensible.</span></h2>
          <p className="text-xl text-theme-text-secondary max-w-2xl mx-auto font-light leading-relaxed">
            We've decoupled health intelligence from physical fulfillment to ensure high-margin SaaS growth with infinite marketplace potential.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Core SaaS */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-10 rounded-[40px] bg-theme-surface border border-theme-primary/30 relative overflow-hidden group shadow-lg shadow-theme-primary/5"
          >
            <div className="absolute inset-0 bg-theme-surface z-0" />
            <img 
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&aspect_ratio=16:9" 
              alt="SaaS Platform" 
              className="absolute inset-0 w-full h-full object-cover opacity-10 group-hover:opacity-20 transition-all duration-500 scale-100 group-hover:scale-110 grayscale group-hover:grayscale-0 z-0"
            />
            <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:opacity-100 transition-opacity z-10">
              <CreditCard className="w-24 h-24 text-theme-primary" />
            </div>
            <div className="relative z-10">
            <h3 className="text-2xl font-black text-theme-text-primary mb-4">Core SaaS</h3>
            <p className="text-theme-text-secondary mb-8 leading-relaxed">The high-margin software layer: Lab monitoring, AI OS insights, and platform access.</p>
            <div className="text-4xl font-black text-theme-text-primary mb-6">$149—$999<span className="text-lg font-normal text-theme-text-muted">/mo</span></div>
            <ul className="space-y-3 text-sm text-theme-text-secondary">
                <li className="flex items-center space-x-3"><Zap className="w-4 h-4 text-theme-primary" /> <span>Monthly Recurring Revenue</span></li>
                <li className="flex items-center space-x-3"><Zap className="w-4 h-4 text-theme-primary" /> <span>80%+ Gross Margins</span></li>
            </ul>
            </div>
          </motion.div>

          {/* Marketplace */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="p-10 rounded-[40px] bg-theme-surface border border-theme-success/30 relative overflow-hidden group shadow-lg shadow-theme-success/5"
          >
            <div className="absolute inset-0 bg-theme-surface z-0" />
            <img 
              src="https://images.unsplash.com/photo-1472851294608-415522f96385?auto=format&fit=crop&q=80&w=800&aspect_ratio=16:9" 
              alt="Marketplace Products" 
              className="absolute inset-0 w-full h-full object-cover opacity-10 group-hover:opacity-20 transition-all duration-500 scale-100 group-hover:scale-110 grayscale group-hover:grayscale-0 z-0"
            />
            <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:opacity-100 transition-opacity z-10">
              <ShoppingBag className="w-24 h-24 text-theme-success" />
            </div>
            <div className="relative z-10">
            <h3 className="text-2xl font-black text-theme-text-primary mb-4">Marketplace</h3>
            <p className="text-theme-text-secondary mb-8 leading-relaxed">The precision ecommerce layer: Custom supplements and wellness products mapped to labs.</p>
            <div className="text-4xl font-black text-theme-text-primary mb-6">15—25%<span className="text-lg font-normal text-theme-text-muted"> commission</span></div>
            <ul className="space-y-3 text-sm text-theme-text-secondary">
                <li className="flex items-center space-x-3"><Zap className="w-4 h-4 text-theme-success" /> <span>High-Convert Bio-Optimization</span></li>
                <li className="flex items-center space-x-3"><Zap className="w-4 h-4 text-theme-success" /> <span>0 Inventory Risk</span></li>
            </ul>
            </div>
          </motion.div>

          {/* Fulfillment */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="p-10 rounded-[40px] bg-theme-surface border border-theme-warning/30 relative overflow-hidden group shadow-lg shadow-theme-warning/5"
          >
            <div className="absolute inset-0 bg-theme-surface z-0" />
            <img 
              src="https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&q=80&w=800&aspect_ratio=16:9" 
              alt="Global Logistics" 
              className="absolute inset-0 w-full h-full object-cover opacity-10 group-hover:opacity-20 transition-all duration-500 scale-100 group-hover:scale-110 grayscale group-hover:grayscale-0 z-0"
            />
            <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:opacity-100 transition-opacity z-10">
              <Truck className="w-24 h-24 text-theme-warning" />
            </div>
            <div className="relative z-10">
            <h3 className="text-2xl font-black text-theme-text-primary mb-4">Fulfillment</h3>
            <p className="text-theme-text-secondary mb-8 leading-relaxed">The modular physical layer: Organic, vegan-forward meal boxes and restaurant protocols.</p>
            <div className="text-4xl font-black text-theme-text-primary mb-6">Modular<span className="text-lg font-normal text-theme-text-muted"> per use</span></div>
            <ul className="space-y-3 text-sm text-theme-text-secondary">
                <li className="flex items-center space-x-3"><Zap className="w-4 h-4 text-theme-warning" /> <span>Partner-Led Logistics</span></li>
                <li className="flex items-center space-x-3"><Zap className="w-4 h-4 text-theme-warning" /> <span>Scalable Lifestyle Upsell</span></li>
            </ul>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default BusinessModelSection;
